// O10 · First day lit (QO10_Lit). Beats: 0 card done (flip + stamp + tiles + confetti), 1 flame ("Your first day is
// lit!"), 2-4 "How this works" (plan here · check-ins · Stuck), 5 plan time (XTime wheel) -> ob.planTime -> O11.
// Admin funnel: o10_lit when it opens (day 0 is lit), o10_time {planTime} on "Sounds good".
import { useEffect, useState } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { C } from '@shared/content/characters';
import { CHECKIN_TEACH, LINES } from '@shared/content/lines';
import { dayKey, stampDate } from '@shared/rules';
import {
  Screen, XScene, XCard, XFace, XTime, Fit, Confetti, PrimaryButton, Bubble, MessageCard, MessageRow, Icon, ScrollArea,
} from '../../components';
import { go, useParams } from '../../router';
import { haptic } from '../../native';
import { timeZone } from '../../api/client';
import { DAY_ONE, TEACH_CARD, TEACH_MORNING, TEACH_PLAN, TEACH_PUSH, firstNote } from '../../content/onboarding';
import { say } from '../../lib/say';
import { obStep, setOb, useDraft } from './draft';
import s from './O10Lit.module.css';

const BEATS = ['card', 'flame', 'teach1', 'teach2', 'teach3', 'time'] as const;
type Beat = (typeof BEATS)[number];
const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);

/** Dev/test entry points: #/O10?mode=lit|flame|teach|time (QO10 wrappers). */
const START: Record<string, number> = { lit: 0, flame: 1, teach: 2, time: 5 };

export function O10Lit({ startBeat }: { startBeat?: number }) {
  const d = useDraft((x) => x);
  const mode = useParams().mode;
  const ch: CharId = d.ch || 'margo';
  const c = C[ch];
  const L = LINES[ch];
  const [bi, setBi] = useState(startBeat ?? (mode && START[mode] != null ? START[mode] : 0));
  const [planTime, setPlanTime] = useState(d.ob.planTime || '9:00 AM');
  const beat: Beat = BEATS[Math.max(0, Math.min(BEATS.length - 1, bi))];
  const first = d.first;
  const later = first?.later || null;
  // The user's own thing ('The Q3 report'): fills {Task} in the buddy's lines and names the done card's note.
  const thing = d.fh?.thing || d.said || first?.card || null;
  const sv = { n: c.name, task: thing };

  useEffect(() => { obStep('o10_lit', {}); }, []);
  useEffect(() => {
    if (beat === 'card') haptic('success');
    if (beat === 'flame') haptic('heavy');
  }, [beat]);

  const next = () => {
    if (beat === 'time') { setOb({ planTime }); obStep('o10_time', { planTime }); go('O11'); return; }
    setBi(bi + 1);
  };
  const label = beat === 'teach3' ? 'Got it' : beat === 'time' ? 'Sounds good' : beat.startsWith('teach') ? 'Next' : 'Continue';
  const teach = beat === 'teach1' || beat === 'teach2' || beat === 'teach3';
  const checkin = d.ob.checkin || 'steady';

  return (
    <Screen theme={ch} bg="#0A0908" id="O10">
      {beat === 'card' && (
        <>
          <XScene ch={ch} stage={1} mode="spot" video={false} alt="" />
          <div class={s.col}>
            <h1 class={s.cardTitle}>Card 1 done!</h1>
            <Fit w={300} h={344} style={{ flex: '0 1 344px', minHeight: 150, marginTop: 21 }}>
              <div style={{ width: 300, height: 344, animation: 'xmFlip 700ms ease-out both' }}>
                <XCard
                  ch={ch} face="back" w={300} h={344} fit="none" label="CARD 1 · DONE" line={say(L.firstDone, sv)}
                  note={first?.note || firstNote(thing)} stamp="DONE" stampSub={stampDate(dayKey(Date.now(), timeZone()))} e={4} tilt={-1.5}
                  stampAnim="xmStamp 650ms 600ms cubic-bezier(.2,.9,.3,1.2) both"
                />
              </div>
            </Fit>
            <div class={s.tiles}>
              {[
                { label: 'POINTS', value: '+10', color: 'var(--acc)', delay: 700 },
                { label: 'TIME', value: first?.time || '2 min', color: '#7FC8A9', delay: 820 },
                { label: 'STREAK', value: 'Day 1', color: '#9DB7F5', delay: 940 },
              ].map((t) => (
                <div key={t.label} class={s.tile} style={{ borderColor: t.color, animation: `xmPop 480ms ${t.delay}ms cubic-bezier(.2,.9,.3,1.3) both` }}>
                  <p class={s.tileHead} style={{ background: t.color }}>{t.label}</p>
                  <p class={s.tileValue} style={{ color: t.color }}>{t.value}</p>
                </div>
              ))}
            </div>
            <div class={s.grow} />
          </div>
        </>
      )}

      {beat === 'flame' && (
        <>
          <div class={s.flameGlow} aria-hidden="true" />
          <div class={s.col}>
            <div class={s.flameBlock}>
              <div class={s.flamePop}>
                <div class={s.flameWobble}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" class={s.flameSvg}><path d="M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2.2 1.2-3.6 2.4-4.6.2 1.6 1 2.6 2.1 2.9C11 9 11.2 6 12 3z" /></svg>
                </div>
              </div>
              <p class={s.big} aria-hidden="true">1</p>
              <h1 class={s.litTitle}>Your first day is lit!</h1>
              <p class={s.litSub}>Do one card a day to keep it going.</p>
            </div>
            <div class={s.grow} />
            <div class={s.dayOne}>
              <XFace ch={ch} e={2} s={44} ring="var(--acc)" alt="" />
              <Bubble from="buddy" ch={ch} bare strong style={{ flex: 1 }} max={9999}>{DAY_ONE[ch]}</Bubble>
            </div>
            <div class={s.grow} style={{ flexGrow: 1.12 }} />
          </div>
        </>
      )}

      {(beat === 'card' || beat === 'flame') && (
        <>
          <div class={s.fullBar} role="progressbar" aria-label="Setup progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={100} />
          <Confetti key={beat} />
        </>
      )}

      {teach && (
        <ScrollArea class={`${s.col} ${s.teach}`}>
          <div class={s.dots} aria-hidden="true">
            {(['teach1', 'teach2', 'teach3'] as Beat[]).map((b) => <span key={b} class={s.dot} data-on={b === beat ? 'true' : 'false'} />)}
          </div>
          <p class={s.howLabel}>HOW THIS WORKS</p>
          <h1 class={s.teachTitle}>
            {beat === 'teach1' ? 'Every morning, we plan your day here.' : beat === 'teach2' ? `${c.name} checks in when it's time.` : 'Stuck? Tap Stuck.'}
          </h1>
          <p class={s.teachBody}>
            {beat === 'teach1' ? `Tell ${c.name} what's on. ${c.name} turns it into small cards with times.`
              : beat === 'teach2' ? CHECKIN_TEACH[checkin]
              : `${c.name} asks what's in the way, then helps: a smaller step, a different start, or doing it together.`}
          </p>
          {beat === 'teach1' && (
            <div class={s.pic} key="t1" style={{ gap: 10 }}>
              <Bubble from="buddy" ch={ch} face e={1} style={{ fontSize: 15 }}>{TEACH_MORNING}</Bubble>
              <MessageCard head="TODAY'S PLAN" indent style={{ borderRadius: 18, padding: '12px 14px' }}>
                {TEACH_PLAN.map((r) => <MessageRow key={r.time} time={r.time} title={r.title} />)}
              </MessageCard>
            </div>
          )}
          {beat === 'teach2' && (
            <div class={s.pic} key="t2">
              <div class={s.push}>
                <div class={s.pushHead}><XFace ch={ch} e={1} s={36} ring="var(--acc)" alt="" /><span>XIAOMAN · 9:30 AM</span></div>
                <p class={s.pushText}><b>{c.name}</b>: {TEACH_PUSH}</p>
              </div>
              <p class={s.setting}><Icon name="bell" size={18} color="var(--acc)" /><span>{`Your setting: ${cap(checkin)}. Change it anytime in Me.`}</span></p>
            </div>
          )}
          {beat === 'teach3' && (
            <div class={s.pic} key="t3" style={{ gap: 10 }}>
              <XCard ch={ch} w={342} h={130} label={TEACH_CARD.label} title={TEACH_CARD.title} titleSize={21} tilt={0} />
              <div class={s.fakeBtns} aria-hidden="true">
                <span class={s.fakeStart}>Start</span>
                <span class={s.fakeStuck}>Stuck</span>
                <span class={s.fakeTrash}><Icon name="trash" size={18} color="#B9B2A6" strokeWidth={1.9} /></span>
              </div>
            </div>
          )}
          <div class={s.grow} />
        </ScrollArea>
      )}

      {beat === 'time' && (
        <>
          <div class={s.timeTop} aria-hidden="true">
            <XScene ch={ch} stage={1} mode="blur" video={false} alt="" />
            <div class={s.timeFade} />
          </div>
          <ScrollArea class={`${s.col} ${s.time}`}>
            <div class={s.ask}>
              <XFace ch={ch} e={4} s={76} ring="var(--acc)" alt="" />
              <p class={s.askBubble}>{say(L.planTimeAsk, sv)}</p>
            </div>
            <h1 class={s.timeTitle}>When should we plan tomorrow?</h1>
            <XTime value={planTime} onPick={setPlanTime} bg="#0A0908" w={342} style={{ alignSelf: 'center', marginTop: 'clamp(12px, 3.6vh, 44px)' }} />
            <p class={s.timeNote}>{`${c.name} will message you then. It takes about a minute.`}</p>
            {later && later.title && (
              <MessageCard variant="dashed" style={{ alignSelf: 'stretch', width: 'auto', marginTop: 16, borderRadius: 16, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span class={s.laterHead}>{(later.when === 'TONIGHT' ? 'ALSO ON TONIGHT · ' : "FIRST ON TOMORROW'S LIST · ") + later.time}</span>
                <span class={s.laterTitle}>{later.title}</span>
              </MessageCard>
            )}
            <div class={s.grow} />
          </ScrollArea>
        </>
      )}

      <div class={s.cta}>
        <PrimaryButton onClick={next} data-cta="next">{label}</PrimaryButton>
      </div>
    </Screen>
  );
}
